import type {Component, DeviceProperties, EditorConfig} from 'grapesjs';
import {EmailMakerCanvasDevice} from '@EmailMakerReact/email-maker-react.types';
import {EMAIL_MAKER_STORAGE_ID} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/storage';

/** `minWidthMedia` is a PMW-only device attribute (GrapesJS core consumes only `widthMedia`); plugin code reads it off the device model. */
type EmailMakerDeviceProperties = DeviceProperties & {
  minWidthMedia?: string;
};

type EmailMakerEditorConfig = Omit<EditorConfig, 'deviceManager'> & {
  deviceManager?: {
    default?: string;
    devices?: EmailMakerDeviceProperties[];
  };
};

const GRAPESJS_CONFIG: EmailMakerEditorConfig = {
  height: '100%',
  // css.clear() runs on new-template load; inline styles survive it, CssComposer rules do not.
  avoidInlineStyle: false,
  forceClass: false,
  canvas: {
    // Doctype-less quirks mode: required so the wrapper table's `height:100% !important` resolves.
    frameContent: '',
    styles: [window.PMW.util.asset_url(`css/email-maker/editor-canvas.css?v=${window.PMW.VERSION}`)],
    // Omit the default badge prefix so GrapesJS doesn't render the model's `icon` string as literal text.
    customBadgeLabel: (component: Component): string => `<span class="toolbar-name"> ${component.getName()}</span>`,
  },
  styleManager: {
    sectors: [],
  },
  storageManager: {
    type: EMAIL_MAKER_STORAGE_ID,
    autosave: false,
    stepsBeforeSave: 1,
  },
  panels: {
    defaults: [],
  },
  selectorManager: {
    componentFirst: true,
  },
  // Without this, device-mode styling emits @media rules instead of base styles; the email maker manages media queries manually.
  devicePreviewMode: true,
  // GrapesJS assigns window.onbeforeunload and never clears it on destroy; useEmailMakerLifecycleGuards owns the dirty-prompt exclusively.
  noticeOnUnload: false,
  deviceManager: {
    default: EmailMakerCanvasDevice.DESKTOP,
    devices: [
      {
        id: EmailMakerCanvasDevice.DESKTOP,
        name: 'Desktop',
        width: '100%',
        minWidthMedia: '767px',
      },
      {
        id: EmailMakerCanvasDevice.TABLET,
        name: 'Tablet',
        width: '600px',
        widthMedia: '768px',
        minWidthMedia: '551px',
      },
      {
        id: EmailMakerCanvasDevice.MOBILE,
        name: 'Mobile',
        width: '345px',
        widthMedia: '550px',
      },
    ],
  },
  // stylePrefix is load-bearing: every RTE SCSS selector depends on the `rte-` prefix.
  richTextEditor: {
    stylePrefix: 'rte-',
    adjustToolbar: true,
    actions: ['bold', 'underline', 'italic', 'strikethrough', 'link'],
  },
  undoManager: {
    trackSelection: false,
  },
  parser: {
    optionsHtml: {
      allowScripts: false,
      allowUnsafeAttr: false,
    },
  },
  plugins: [],
};

export {GRAPESJS_CONFIG as default};
